import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LightbulbIcon, TargetIcon, FlameIcon, LockIcon } from 'lucide-react';
import { DjayAvatar, DjayEmotion, DjayVariant } from './DjayAvatar';
import { DjayBubble } from './DjayBubble';
import { useDjaySelection } from './useDjaySelection';
import { useAppStore } from '../app/hooks/useAppStore';
import { useMotionBudget } from '../app/hooks/useMotionBudget';
import { useClickOutside } from '../app/hooks/useClickOutside';
import { soundManager } from '../utils/soundManager';
import { dataLayer } from '../data/dataLayer';
import { MODULES } from '../content';
import { getDjayGreeting, getDjayViewMessage, getDjayDailyTip } from './djayMessages';
interface FloatingDjayProps {
  currentView?: string;
  onNavigate?: (path: string) => void;
}
export function FloatingDjay({
  currentView,
  onNavigate
}: FloatingDjayProps) {
  const {
    state,
    activeAccount,
    isDemoMode,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  const [showEasterEggLabel, setShowEasterEggLabel] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState<string | null>(null);
  const [bubbleAutoDismiss, setBubbleAutoDismiss] = useState<number | undefined>(undefined);
  const [emotion, setEmotion] = useState<DjayEmotion>('happy');
  const [isPulsing, setIsPulsing] = useState(false);
  const [tapPulse, setTapPulse] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isLongPressTriggered = useRef(false);
  const hasShownWelcomeRef = useRef(false);
  const lastViewRef = useRef<string | undefined>(currentView);
  const streak = state?.streak?.currentTotalStreak || 0;
  const displayName = activeAccount?.displayName || 'vriend';
  // Load easter egg state
  useEffect(() => {
    if (activeAccount?.id) {
      dataLayer.getDjayLongPressDiscovered(activeAccount.id).then(setHasDiscoveredLongPress);
    }
  }, [activeAccount?.id]);
  // Calculate unlock conditions
  const {
    masteredCount,
    totalQuestionsAnswered
  } = useMemo(() => {
    let mastered = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      totalQs += answered;
      if (mastery >= 95) mastered++;
    });
    return {
      masteredCount: mastered,
      totalQuestionsAnswered: totalQs
    };
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  const consecutiveFullWeeks = state?.dailyStreak?.consecutiveFullWeeks || 0;
  const unlockedVariants = useMemo(() => {
    return [{
      id: 'default' as DjayVariant,
      name: 'Standaard',
      unlocked: true
    }, {
      id: 'pixel' as DjayVariant,
      name: 'DJ Pixel',
      unlocked: totalQuestionsAnswered >= 100
    }, {
      id: 'blaze' as DjayVariant,
      name: 'DJ Blaze',
      unlocked: consecutiveFullWeeks >= 3
    }, {
      id: 'frost' as DjayVariant,
      name: 'DJ Frost',
      unlocked: masteredCount >= 3
    }, {
      id: 'royal' as DjayVariant,
      name: 'DJ Royal',
      unlocked: masteredCount >= MODULES.length
    }, {
      id: 'cosmic' as DjayVariant,
      name: 'DJ Cosmic',
      unlocked: (state?.stats?.level || 0) >= 15
    }, {
      id: 'shadow' as DjayVariant,
      name: 'DJ Shadow',
      unlocked: (state?.stats?.xp || 0) >= 30000
    }, {
      id: 'goud' as DjayVariant,
      name: 'DJ Goud',
      unlocked: hasDiscoveredLongPress
    }];
  }, [totalQuestionsAnswered, consecutiveFullWeeks, masteredCount, state?.stats?.level, state?.stats?.xp, hasDiscoveredLongPress]);
  useClickOutside(panelRef, () => {
    if (isPanelOpen) setIsPanelOpen(false);
    if (isLongPressMenuOpen) setIsLongPressMenuOpen(false);
  });
  // Helper to show a proactive message with an attention pulse first
  const showProactiveMessage = (msg: string, newEmotion: DjayEmotion, duration: number) => {
    if (isPanelOpen || isLongPressMenuOpen) return;
    setIsPulsing(true);
    setEmotion(newEmotion);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => {
      setIsPulsing(false);
      setBubbleMessage(msg);
      setBubbleAutoDismiss(duration);
      soundManager.playDjaySpeak();
    }, 1000);
  };
  // 1. Welcome on first load
  useEffect(() => {
    if (!hasShownWelcomeRef.current && activeAccount) {
      hasShownWelcomeRef.current = true;
      const greeting = getDjayGreeting(displayName);
      setTimeout(() => {
        showProactiveMessage(greeting.text, greeting.emotion, 4000);
      }, 1000);
    }
  }, [activeAccount, displayName]);
  // 2. View change reactions
  useEffect(() => {
    if (currentView && currentView !== lastViewRef.current && currentView !== 'practice') {
      lastViewRef.current = currentView;
      const viewMsg = getDjayViewMessage(currentView, {
        streak,
        displayName
      });
      showProactiveMessage(viewMsg.text, viewMsg.emotion, 3000);
    }
  }, [currentView, streak, displayName]);
  // 3. Idle detection (2 minutes)
  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        showProactiveMessage('Zullen we verder gaan?', 'thinking', 5000);
      }, 2 * 60 * 1000);
    };
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    resetIdle();
    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);
  const handlePointerDown = () => {
    isLongPressTriggered.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressTriggered.current = true;
      setIsPanelOpen(false);
      setIsLongPressMenuOpen(true);
      // Easter egg logic
      if (!hasDiscoveredLongPress && activeAccount?.id) {
        setHasDiscoveredLongPress(true);
        dataLayer.saveDjayLongPressDiscovered(activeAccount.id, true);
        setShowEasterEggLabel(true);
        setTimeout(() => setShowEasterEggLabel(false), 4000);
        setEmotion('celebrating');
      } else {
        setEmotion('excited');
      }
      soundManager.playDjaySpeak();
      // Haptic feedback if available
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(50);
      }
    }, 500); // 500ms for long press
  };
  const handlePointerUp = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  };
  const handleDjayClick = () => {
    if (isLongPressTriggered.current) return; // Skip if it was a long press
    setTapPulse(true);
    setTimeout(() => setTapPulse(false), 500);
    if (isLongPressMenuOpen) {
      setIsLongPressMenuOpen(false);
      return;
    }
    if (isPanelOpen) {
      setIsPanelOpen(false);
      return;
    }
    setBubbleMessage(null);
    setIsPulsing(false);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    const viewMsg = getDjayViewMessage(currentView || 'home', {
      streak,
      displayName
    });
    setEmotion(viewMsg.emotion);
    setIsPanelOpen(true);
    soundManager.playDjaySpeak();
  };
  const handleTipClick = () => {
    const tip = getDjayDailyTip();
    setEmotion(tip.emotion);
    setIsPanelOpen(false);
    setBubbleMessage(tip.text);
    setBubbleAutoDismiss(5000);
    soundManager.playDjaySpeak();
  };
  const handleNavClick = (path: string) => {
    setIsLongPressMenuOpen(false);
    setIsPanelOpen(false);
    if (onNavigate) onNavigate(path);
  };
  if (currentView === 'practice' || !activeAccount && !isDemoMode) {
    return null;
  }
  const greetingMsg = getDjayViewMessage(currentView || 'home', {
    streak,
    displayName
  });
  return <motion.div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 flex items-end justify-end pointer-events-none" initial={shouldAnimate ? {
    y: 100,
    opacity: 0
  } : false} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    damping: 15,
    stiffness: 200
  }} data-id="element-2953">
      <div className="relative flex items-end pointer-events-auto" ref={panelRef} data-id="element-2954">
        {/* Expanded Click Panel */}
        <AnimatePresence data-id="element-2955">
          {isPanelOpen && !isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.2
        }} className="absolute bottom-full right-0 mb-4 w-64 md:w-72 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right" data-id="element-2956">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-2957" />

              <div className="relative z-10" data-id="element-2958">
                <p className="text-slate-800 font-medium mb-3 text-sm md:text-base" data-id="element-2959">
                  {greetingMsg.text}
                </p>

                <div className="space-y-2" data-id="element-2960">
                  <button onClick={handleTipClick} className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-sm font-medium transition-colors" data-id="element-2961">
                    <LightbulbIcon className="w-4 h-4" data-id="element-2962" />
                    Dagelijkse tip
                  </button>

                  <button onClick={() => handleNavClick('learningLab')} disabled={currentView !== 'home'} className="w-full flex items-center gap-2 px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-orange-50" data-id="element-2963">
                    <TargetIcon className="w-4 h-4" data-id="element-2964" />
                    Aanbeveling
                  </button>

                  <div className="w-full flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-medium" data-id="element-2965">
                    <FlameIcon className="w-4 h-4 text-orange-500" data-id="element-2966" />
                    Mijn streak: {streak} dagen
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Long Press Quick Nav Menu */}
        <AnimatePresence data-id="element-2967">
          {isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.15,
          ease: [0.32, 0.72, 0, 1]
        }} className="absolute bottom-full right-0 mb-4 w-[280px] md:w-[340px] bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right pointer-events-auto" data-id="element-2968">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-2969" />

              <div className="relative z-10" data-id="element-2970">
                <p className="text-xs text-slate-400 font-medium mb-3" data-id="element-2971">
                  Kies je stijl
                </p>

                {/* Easter Egg Label */}
                <AnimatePresence data-id="element-2972">
                  {showEasterEggLabel && <motion.div initial={{
                opacity: 0,
                height: 0,
                marginBottom: 0
              }} animate={{
                opacity: 1,
                height: 'auto',
                marginBottom: 12
              }} exit={{
                opacity: 0,
                height: 0,
                marginBottom: 0
              }} className="bg-amber-50 text-amber-800 text-xs font-medium px-3 py-2 rounded-lg overflow-hidden" data-id="element-2973">
                      Geheim ontdekt! DJ Goud ontgrendeld! ✨
                    </motion.div>}
                </AnimatePresence>

                <div className="flex items-start gap-3 overflow-x-auto pb-2" style={{
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }} data-id="element-2974">
                  <style data-id="element-2975">{`
                    .scrollbar-hide::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                  <div className="flex gap-3 scrollbar-hide" data-id="element-2976">
                    {unlockedVariants.map(v => {
                  const isSelected = selectedVariant === v.id;
                  return <button key={v.id} onClick={() => {
                    if (v.unlocked) {
                      setSelectedVariant(v.id);
                      setIsLongPressMenuOpen(false);
                    }
                  }} disabled={!v.unlocked} className="flex flex-col items-center gap-1.5 flex-shrink-0 group focus:outline-none" title={v.unlocked ? v.name : `${v.name} (Vergrendeld)`} aria-label={`Kies ${v.name} stijl`} data-id="element-2977">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 relative
                              ${isSelected ? 'ring-2 ring-orange-400 scale-105 bg-orange-50 shadow-sm' : 'bg-slate-50 border border-slate-100 shadow-sm group-hover:bg-slate-100'}
                              ${!v.unlocked ? 'opacity-50 grayscale cursor-not-allowed' : 'cursor-pointer'}
                            `} data-id="element-2978">
                            <div className="w-8 h-8" data-id="element-2979">
                              <DjayAvatar variant={v.id} emotion="happy" size="sm" data-id="element-2980" />
                            </div>
                            {!v.unlocked && <div className="absolute inset-0 bg-slate-900/5 rounded-full flex items-center justify-center backdrop-blur-[0.5px]" data-id="element-2981">
                                <LockIcon className="w-4 h-4 text-slate-500 drop-shadow-sm" data-id="element-2982" />
                              </div>}
                          </div>
                          <span className={`text-[10px] font-medium transition-colors ${isSelected ? 'text-orange-600' : 'text-slate-500'}`} data-id="element-2983">
                            {v.name}
                          </span>
                        </button>;
                })}
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Simple Bubble (Proactive or Tip) */}
        <div className="absolute bottom-full right-0 mb-2 origin-bottom-right" data-id="element-2984">
          <DjayBubble message={bubbleMessage || ''} isVisible={!!bubbleMessage && !isPanelOpen && !isLongPressMenuOpen && !showEasterEggLabel} onDismiss={() => setBubbleMessage(null)} autoDismissMs={bubbleAutoDismiss} position="bottom" data-id="element-2985" />
        </div>

        {/* Avatar Button */}
        <motion.button onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} onClick={handleDjayClick} className="relative focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300/50 rounded-full select-none" aria-label="Praat met Djay" whileHover={shouldAnimate ? {
        scale: 1.05
      } : undefined} whileTap={shouldAnimate ? {
        scale: 0.9,
        y: 4
      } : undefined} animate={shouldAnimate && !isPanelOpen && !isLongPressMenuOpen && !bubbleMessage ? {
        y: [0, -4, 0],
        transition: {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      } : undefined} style={{
        willChange: 'transform',
        touchAction: 'none'
      }} data-id="element-2986">
          {/* Tap Pulse */}
          <AnimatePresence data-id="element-2987">
            {tapPulse && <motion.div initial={{
            opacity: 0.8,
            scale: 1
          }} animate={{
            opacity: 0,
            scale: 1.5
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="absolute inset-0 border-4 border-orange-300 rounded-full" data-id="element-2988" />}
          </AnimatePresence>

          {/* Attention Pulse Glow */}
          <AnimatePresence data-id="element-2989">
            {isPulsing && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: [0.4, 0.8, 0.4],
            scale: [1, 1.2, 1]
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="absolute inset-0 bg-orange-400 rounded-full blur-md" data-id="element-2990" />}
          </AnimatePresence>

          {/* Shadow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/10 rounded-full blur-sm" data-id="element-2991" />

          <div className="w-12 h-12 md:w-16 md:h-16 relative z-10" data-id="element-2992">
            <DjayAvatar emotion={emotion} variant={selectedVariant} size="xl" className="w-full h-full drop-shadow-md" animate={shouldAnimate} data-id="element-2993" />
          </div>
        </motion.button>
      </div>
    </motion.div>;
}